import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef, useRef, useMemo, createElement, useEffect, useCallback } from "react";
import { forceNumber, Tooltip } from "@flatfile/design-system";
import { useTranslation } from "react-i18next";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeGrid } from "react-window";
import { useTheme } from "styled-components";
import Cell from "../Cell/Cell.js";
import { useBulkRowSelection } from "../../context/useBulkRowSelection.js";
import { useColumns } from "../../context/useColumnsContext.js";
import { useDataBufferContext } from "../../context/useDataBuffer.js";
import { useTableScroller } from "../../context/useTableScroller.js";
import { useVirtualizedTableProps, VirtualizedTablePropsProvider } from "../../context/useVirtualizedTableProps.js";
import { useSelectCells, getSelectedCellRange } from "../../hooks/useSelectCells.js";
import { EVerticalScrollDirection } from "../../utils/buffer/types.js";
import { rowHeaderTooltipId, columnHeaderTooltipId, columnHeaderClickableTooltipId } from "../../utils/utils.js";
import { useEditorContext } from "../../context/useEditorContext.js";
import { useColumnSelectionFocus } from "../../hooks/useColumnSelectionFocus.js";
import useUnsafeEffect from "../../hooks/useUnsafeEffect.js";
import { AddNewRecordRow } from "./AddNewRecordRow.js";
import { INDEX_COLUMN_INDEX, TABLE_ROW_LIMIT, TABLE_HEIGHT_LIMIT_FACTOR, OVERSCAN_ROW_COUNT, OVERSCAN_COLUMN_COUNT, TABLE_HIDDEN_ROWS_AT_LIMIT } from "./constants.js";
import { InnerTable, PinnedColumnElement, HeaderRowElement, TableToolsContainer, TableHeightRootStyle, PinnedColumnsWrapper, MaximumSizeMessage, MaximumMessageInner, OuterTableElement } from "./Styles.js";
import TableFooter from "./TableFooter.js";
import { getCellKey, getPinnedColumnsWidth, getAverageColumnWidth, getHasVariableColumns } from "./utils.js";
const MemoizedCell = React.memo(Cell);
const MemoizedInnerTable = React.memo(InnerTable);
const MemoizedPinnedColumnElement = React.memo(PinnedColumnElement);
const MemoizedHeaderRowElement = React.memo(HeaderRowElement);
const EMPTY = {};
const PinnedColumn = (props) => {
  const { rowIndex, style } = props.firstChildProps;
  const columnStyle = props.itemData.columnStyles[props.columnIndex];
  const firstRenderedRowIndex = rowIndex || 0;
  const lastPossibleRowIndex = TABLE_ROW_LIMIT + props.itemData.additionalRows;
  let lastRenderedRowIndex = props.visibleRowCount + OVERSCAN_ROW_COUNT * 2 + rowIndex;
  if (lastRenderedRowIndex > lastPossibleRowIndex) {
    lastRenderedRowIndex = lastPossibleRowIndex;
  }
  const indexCornerStyle = useMemo(
    () => ({ height: props.itemData.rowHeight }),
    [props.itemData.rowHeight]
  );
  return /* @__PURE__ */ jsxs(
    MemoizedPinnedColumnElement,
    {
      index: props.columnIndex === -1,
      width: columnStyle == null ? void 0 : columnStyle.width,
      left: columnStyle == null ? void 0 : columnStyle.left,
      children: [
        /* @__PURE__ */ jsx(
          MemoizedCell,
          {
            data: props.itemData,
            rowIndex: 0,
            columnIndex: props.columnIndex,
            style: indexCornerStyle,
            pinned: true,
            header: true
          },
          getCellKey(0, props.columnIndex, "pinned")
        ),
        props.totalRowArray.slice(firstRenderedRowIndex, lastRenderedRowIndex).map((row, index) => {
          const rowIndex2 = index + firstRenderedRowIndex + 1;
          return /* @__PURE__ */ jsx(
            MemoizedCell,
            {
              data: props.itemData,
              rowIndex: rowIndex2,
              columnIndex: props.columnIndex,
              style: { ...style, top: style.height * rowIndex2 },
              pinned: true
            },
            getCellKey(rowIndex2, props.columnIndex, "pinned")
          );
        })
      ]
    }
  );
};
const IndexRow = (props) => {
  const { columnIndex } = props.firstChildProps;
  const firstRenderedColumnIndex = columnIndex || 0;
  const lastRenderedColumnIndex = props.visibleColumnCount + OVERSCAN_COLUMN_COUNT * 2 + columnIndex;
  const indexRowCellStyle = useMemo(
    () => ({
      height: props.itemData.rowHeight,
      position: "absolute",
      top: 0
    }),
    [props.itemData.rowHeight]
  );
  return /* @__PURE__ */ jsxs(
    MemoizedHeaderRowElement,
    {
      height: props.itemData.rowHeight,
      width: props.width,
      showSkeleton: props.showSkeleton,
      children: [
        /* @__PURE__ */ jsx(
          MemoizedCell,
          {
            data: props.itemData,
            columnIndex: INDEX_COLUMN_INDEX,
            rowIndex: 0,
            style: indexRowCellStyle,
            header: true
          },
          getCellKey(0, INDEX_COLUMN_INDEX, "indexRow")
        ),
        props.itemData.columns.slice(firstRenderedColumnIndex, lastRenderedColumnIndex).map((column, index) => {
          const columnIndex2 = index + firstRenderedColumnIndex;
          return /* @__PURE__ */ jsx(
            MemoizedCell,
            {
              data: props.itemData,
              columnIndex: columnIndex2,
              rowIndex: 0,
              style: indexRowCellStyle,
              header: true
            },
            getCellKey(0, columnIndex2, "indexRow")
          );
        })
      ]
    }
  );
};
const ColumnMetaRow = (props) => {
  const { columnIndex } = props.firstChildProps;
  const firstRenderedColumnIndex = columnIndex || 0;
  const lastRenderedColumnIndex = props.visibleColumnCount + OVERSCAN_COLUMN_COUNT * 2 + columnIndex;
  const indexRowCellStyle = useMemo(
    () => ({
      height: props.itemData.rowHeight,
      position: "absolute",
      top: 0
    }),
    [props.itemData.rowHeight]
  );
  return /* @__PURE__ */ jsxs(
    MemoizedHeaderRowElement,
    {
      height: props.itemData.rowHeight,
      width: props.width,
      showSkeleton: false,
      children: [
        /* @__PURE__ */ jsx(
          MemoizedCell,
          {
            data: props.itemData,
            columnIndex: INDEX_COLUMN_INDEX,
            rowIndex: 0,
            style: indexRowCellStyle,
            header: true
          },
          getCellKey(0, INDEX_COLUMN_INDEX, "metaRow")
        ),
        props.itemData.columns.slice(firstRenderedColumnIndex, lastRenderedColumnIndex).map((column, index) => {
          const columnIndex2 = index + firstRenderedColumnIndex;
          const columnStyle = props.itemData.columnStyles[columnIndex2];
          return props.MetaComponent ? /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                ...indexRowCellStyle,
                left: columnStyle.left,
                width: columnStyle.width
              },
              children: /* @__PURE__ */ jsx(props.MetaComponent, { fieldName: column.value })
            },
            getCellKey(0, columnIndex2, "metaRow")
          ) : null;
        })
      ]
    }
  );
};
const TableMax = ({
  width,
  hiddenRows,
  emptyHiddenRowCount
}) => {
  const { t } = useTranslation();
  const hiddenRowsWithRecords = hiddenRows - emptyHiddenRowCount;
  return /* @__PURE__ */ jsx(MaximumSizeMessage, { children: /* @__PURE__ */ jsx(MaximumMessageInner, { $width: width, children: t(
    hiddenRows === 1 ? "sheet.table.maximumMessage.singular" : "sheet.table.maximumMessage.plural",
    { hiddenRows: hiddenRowsWithRecords.toLocaleString() }
  ) }) });
};
const InnerTableElement = forwardRef(({ children, style, ...otherProps }, refProp) => {
  var _a, _b;
  const ref = useRef(null);
  const footerRef = useRef(null);
  const {
    height,
    width,
    pinnedColumnsWidth,
    averageColumnWidth,
    hasVariableColumnWidths,
    itemData,
    totalRowArray,
    pinnedColumns = [],
    columnMetaComponent,
    readOnly,
    canAddRows
  } = useVirtualizedTableProps();
  const isEmpty = !totalRowArray.length;
  const isScrolling = style.pointerEvents === "none";
  const hasToolMenuSlot = !!itemData.toolMenuSlot;
  const styleWithExtraWidth = useMemo(
    () => ({
      ...style,
      width: style.width + (hasToolMenuSlot ? 32 : 0) + itemData.theme.table.rightOverflowGutter
    }),
    [style, itemData.toolMenuSlot, itemData.theme.table.rightOverflowGutter]
  );
  const visibleRowCount = Math.round(height / itemData.rowHeight);
  const visibleColumnCount = Math.round(width / averageColumnWidth);
  const firstChildProps = children && ((_a = children[0]) == null ? void 0 : _a.props) ? children[0].props : EMPTY;
  const pinned = useMemo(
    () => [INDEX_COLUMN_INDEX, ...pinnedColumns],
    [pinnedColumns]
  );
  const scrollableHeight = ((_b = ref == null ? void 0 : ref.current) == null ? void 0 : _b.offsetHeight) ?? 0;
  const scrollableRows = Math.round(scrollableHeight / itemData.rowHeight);
  const hiddenRows = itemData.totalRowCount > TABLE_ROW_LIMIT ? itemData.totalRowCount - TABLE_ROW_LIMIT : itemData.totalRowCount - scrollableRows;
  const emptyHiddenRowCount = columnMetaComponent ? 2 : 1;
  const heightLimit = TABLE_HEIGHT_LIMIT_FACTOR * itemData.rowHeight;
  const heightControl = forceNumber(
    style.height > heightLimit ? heightLimit : style.height
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    hasToolMenuSlot && /* @__PURE__ */ jsx(TableToolsContainer, { children: /* @__PURE__ */ jsx("div", { children: itemData.toolMenuSlot }) }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: refProp,
        style: {
          position: "relative",
          height: "100%"
        },
        children: [
          /* @__PURE__ */ jsx(TableHeightRootStyle, { height: heightControl }),
          /* @__PURE__ */ createElement(
            MemoizedInnerTable,
            {
              ...otherProps,
              ref,
              key: `${pinnedColumns.join("-")}-count:${totalRowArray.length}`,
              style: {
                ...styleWithExtraWidth,
                height: heightControl - itemData.rowHeight,
                maxHeight: heightControl - itemData.rowHeight,
                marginBottom: `${itemData.rowHeight}px`
              },
              width: styleWithExtraWidth.width,
              height: heightControl,
              showSkeleton: !hasVariableColumnWidths && !isEmpty,
              "data-tableid": itemData.tableId,
              "data-testid": "tt-inner-table",
              canAddRows
            },
            !isEmpty && /* @__PURE__ */ jsxs(Fragment, { children: [
              !isScrolling && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(
                  Tooltip,
                  {
                    id: rowHeaderTooltipId(itemData.tableId),
                    place: "right",
                    className: "tooltip table-tooltip",
                    positionStrategy: "fixed"
                  },
                  rowHeaderTooltipId(itemData.tableId)
                ),
                /* @__PURE__ */ jsx(
                  Tooltip,
                  {
                    id: columnHeaderTooltipId(itemData.tableId),
                    place: "bottom",
                    className: "tooltip table-tooltip",
                    positionStrategy: "fixed"
                  },
                  columnHeaderTooltipId(itemData.tableId)
                ),
                /* @__PURE__ */ jsx(
                  Tooltip,
                  {
                    id: columnHeaderClickableTooltipId(itemData.tableId),
                    place: "bottom",
                    className: "tooltip table-tooltip",
                    positionStrategy: "fixed",
                    clickable: true
                  },
                  columnHeaderClickableTooltipId(itemData.tableId)
                )
              ] }),
              columnMetaComponent && /* @__PURE__ */ jsx(
                ColumnMetaRow,
                {
                  itemData,
                  width: styleWithExtraWidth.width,
                  height: itemData.rowHeight,
                  firstChildProps,
                  visibleColumnCount,
                  showSkeleton: !hasVariableColumnWidths,
                  MetaComponent: columnMetaComponent
                }
              ),
              /* @__PURE__ */ jsx(
                IndexRow,
                {
                  itemData,
                  width: styleWithExtraWidth.width,
                  height: itemData.rowHeight,
                  firstChildProps,
                  visibleColumnCount,
                  showSkeleton: !hasVariableColumnWidths
                }
              ),
              /* @__PURE__ */ jsx(
                PinnedColumnsWrapper,
                {
                  offset: itemData.rowHeight,
                  width: pinnedColumnsWidth,
                  hasPinnedColumns: pinned.length > 1,
                  children: pinned.map((pinnedIndex, columnIndex) => /* @__PURE__ */ jsx(
                    PinnedColumn,
                    {
                      itemData,
                      pinnedIndex,
                      columnIndex: columnIndex - 1,
                      totalRowArray,
                      visibleRowCount,
                      firstChildProps
                    },
                    `${columnIndex - 1}-pinned-column`
                  ))
                }
              ),
              itemData.decorateFooterCell && /* @__PURE__ */ jsx(
                TableFooter,
                {
                  columns: itemData.columns,
                  decorateFooterCell: itemData.decorateFooterCell,
                  offset: itemData.columnStyles[0].left,
                  height: itemData.rowHeight
                }
              ),
              children
            ] }),
            scrollableHeight > height && hiddenRows > 0 ? /* @__PURE__ */ jsx(
              TableMax,
              {
                width,
                hiddenRows,
                emptyHiddenRowCount
              }
            ) : null
          ),
          !readOnly && canAddRows && /* @__PURE__ */ jsx(
            AddNewRecordRow,
            {
              ref: footerRef,
              itemData,
              width: styleWithExtraWidth.width,
              height: itemData.rowHeight,
              firstChildProps,
              visibleColumnCount,
              showSkeleton: !hasVariableColumnWidths,
              pinnedColumns,
              columnIndex: INDEX_COLUMN_INDEX,
              totalRowArray,
              visibleRowCount
            }
          )
        ]
      }
    )
  ] });
});
InnerTableElement.displayName = "InnerTableElement";
const VirtualizedTable = ({
  additionalRows,
  cellClassName,
  columnSelectionEnabled,
  columnContextMenuSlot,
  decorateCell,
  decorateFooterCell,
  emptyState,
  height: defaultHeight,
  innerTableRef,
  isDiffView,
  isLoading,
  onCellClick,
  onFindAndReplace,
  onScroll,
  onSearchValue,
  onSelectedRows,
  onSelectionChange,
  onSort,
  pinningEnabled,
  readOnly,
  rowSelectionType,
  selectedRows,
  setTableIsEditing,
  showColumnMenus,
  sortOverride,
  tableId,
  tableRef,
  totalRowCount,
  useFilterByValue,
  width: defaultWidth,
  columnMetaComponent,
  canAddRows,
  canInsertRows,
  cellContextMenuSlot,
  toolMenuSlot,
  headerInjections
}) => {
  const { scrollableRef, scrollToItem } = useTableScroller();
  const theme = useTheme();
  const { buffer } = useDataBufferContext();
  const { selection, exitSelectionState } = useBulkRowSelection();
  const {
    columnConfig,
    columnStyles,
    pinnedColumns,
    columnDiff,
    columnClassNames
  } = useColumns();
  const rowCount = totalRowCount + additionalRows;
  const columnCount = columnConfig.length;
  const {
    onCellMouseDown,
    onCellMouseUp,
    onCellMouseEnter,
    onCellFocus,
    onCellTab,
    onCellArrowRight,
    onCellArrowLeft,
    onCellArrowUp,
    onCellArrowDown,
    onSelectRows,
    isSelecting,
    clearCellSelection
  } = useSelectCells(
    tableRef,
    theme,
    columnCount,
    rowCount,
    tableId,
    selectedRows
  );
  useColumnSelectionFocus();
  const itemData = useMemo(
    () => ({
      tableId,
      additionalRows,
      rowSelectionType,
      pinningEnabled,
      columnSelectionEnabled,
      cellClassName,
      columns: columnConfig,
      columnDiff,
      columnStyles,
      columnClassNames,
      isDiffView,
      onCellArrowDown,
      onCellArrowLeft,
      onCellArrowRight,
      onCellArrowUp,
      onCellFocus,
      onCellMouseDown,
      onCellMouseEnter,
      onCellMouseUp,
      onCellTab,
      onCellClick,
      onFindAndReplace,
      onSelectedRows,
      onSearchValue,
      onSort,
      pinnedColumns,
      readOnly,
      scrollToItem,
      sortOverride,
      theme,
      totalRowCount,
      exitSelectionState,
      clearCellSelection,
      useFilterByValue,
      isLoading,
      showColumnMenus,
      decorateCell,
      decorateFooterCell,
      columnContextMenuSlot,
      hasColumnMetaRow: !!columnMetaComponent,
      rowHeight: theme.rowHeight,
      canInsertRows: canAddRows && canInsertRows,
      cellContextMenuSlot,
      toolMenuSlot,
      headerInjections
    }),
    [
      columnConfig,
      columnDiff,
      columnStyles,
      columnClassNames,
      pinnedColumns,
      totalRowCount,
      sortOverride,
      readOnly,
      exitSelectionState,
      theme.indexColumn.width,
      isLoading,
      columnSelectionEnabled,
      pinningEnabled,
      showColumnMenus,
      decorateCell,
      columnContextMenuSlot,
      columnMetaComponent,
      theme.rowHeight,
      canInsertRows,
      cellContextMenuSlot,
      toolMenuSlot,
      headerInjections
    ]
  );
  const pinnedColumnsWidth = useMemo(
    () => getPinnedColumnsWidth(itemData),
    [columnConfig, columnStyles, pinnedColumns, theme.indexColumn.width]
  );
  const averageColumnWidth = useMemo(
    () => getAverageColumnWidth(itemData),
    [columnConfig, columnStyles]
  );
  const hasVariableColumnWidths = useMemo(
    () => getHasVariableColumns(itemData),
    [columnConfig, columnStyles]
  );
  const totalRowArray = useMemo(
    () => Array.from({ length: rowCount }),
    [rowCount]
  );
  useEffect(() => {
    buffer.rowHeight = theme.rowHeight;
  }, [theme.rowHeight]);
  const handleScroll = useCallback(
    (height) => ({
      scrollTop,
      verticalScrollDirection
    }) => {
      const scrollDirection = EVerticalScrollDirection[verticalScrollDirection];
      buffer.onScrollTable(scrollTop, scrollDirection, height);
      onScroll == null ? void 0 : onScroll();
    },
    []
  );
  useEffect(() => {
    if (selectedRows) {
      onSelectRows(selectedRows);
    }
  }, [tableRef, selectedRows]);
  const { activeEditor } = useEditorContext();
  const isEditing = !!activeEditor;
  useUnsafeEffect(() => {
    if (!isSelecting) {
      const selections = [];
      const selectedCellRange = getSelectedCellRange({ sort: true, tableId });
      if (selectedCellRange) {
        const start = selectedCellRange[0];
        const end = selectedCellRange[1];
        const startRowIndex = start[0] - 1 === -1 ? 0 : start[0] - 1;
        for (let rowIndex = startRowIndex; rowIndex < end[0]; rowIndex++) {
          const row = buffer.getRow(rowIndex);
          if (row && row.id) {
            selections.push({
              id: row.id,
              isEditing,
              row,
              // To properly handle hidden columns and pinning, we need to look
              // at the column config index
              cells: Array.from({ length: end[1] - start[1] + 1 }).map((_, index) => {
                if (columnConfig[index + start[1]] && Object.hasOwn(columnConfig[index + start[1]], "index")) {
                  return columnConfig[index + start[1]].index;
                }
              }).filter((int) => int !== void 0)
            });
          }
        }
      }
      onSelectionChange == null ? void 0 : onSelectionChange(selections);
    }
  }, [isSelecting, isEditing, columnConfig]);
  useUnsafeEffect(() => {
    const exceptions = Object.values(selection.exceptions).filter(Boolean);
    if (selection.type === "all" || selection.type === "none" && exceptions.length > 0) {
      const selections = exceptions.map((exception) => {
        if (exception == null ? void 0 : exception.id) {
          return {
            id: exception.id,
            isEditing: false,
            cells: [],
            row: exception
          };
        }
      }).filter(Boolean);
      onSelectionChange == null ? void 0 : onSelectionChange(selections);
    } else {
      onSelectionChange == null ? void 0 : onSelectionChange([]);
    }
  }, [selection, tableId, buffer]);
  useEffect(() => {
    setTableIsEditing == null ? void 0 : setTableIsEditing(isEditing);
  }, [isEditing]);
  const gridRowCount = useMemo(() => {
    return totalRowCount > TABLE_ROW_LIMIT ? TABLE_ROW_LIMIT + TABLE_HIDDEN_ROWS_AT_LIMIT + (columnMetaComponent ? 2 : 1) : rowCount + (columnMetaComponent ? 2 : 1);
  }, [rowCount, totalRowCount]);
  return /* @__PURE__ */ jsx(AutoSizer, { doNotBailOutOnEmptyChildren: true, children: ({ height, width }) => {
    const displayHeight = defaultHeight || height;
    const displayWidth = defaultWidth || width;
    return /* @__PURE__ */ jsx(
      VirtualizedTablePropsProvider,
      {
        height: displayHeight,
        width: displayWidth,
        itemData,
        pinnedColumns,
        pinnedColumnsWidth,
        averageColumnWidth,
        hasVariableColumnWidths,
        totalRowArray,
        tableRef,
        columnMetaComponent,
        readOnly,
        canAddRows,
        children: /* @__PURE__ */ jsx(
          FixedSizeGrid,
          {
            ref: scrollableRef,
            itemData,
            columnWidth: averageColumnWidth,
            rowHeight: itemData.rowHeight,
            columnCount,
            rowCount: gridRowCount,
            overscanColumnCount: OVERSCAN_COLUMN_COUNT,
            overscanRowCount: OVERSCAN_ROW_COUNT,
            outerRef: tableRef,
            innerRef: innerTableRef,
            outerElementType: OuterTableElement,
            innerElementType: InnerTableElement,
            onScroll: handleScroll(displayHeight),
            height: displayHeight,
            width: displayWidth,
            children: MemoizedCell
          }
        )
      }
    );
  } });
};
export {
  VirtualizedTable
};
